import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, ArrowLeft, AlertTriangle, Ban, UserX, FileWarning, Scale, CheckCircle, XCircle } from 'lucide-react';

export default function AcceptableUse() {
  const lastUpdated = "April 4, 2026";

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(180deg, #0A0A1B 0%, #0D0D1F 100%)' }}>
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl border-b" style={{ background: 'rgba(10, 10, 27, 0.8)', borderColor: 'rgba(255,255,255,0.05)' }}>
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-white hover:opacity-80 transition-opacity">
            <ArrowLeft className="h-5 w-5" />
            <span className="font-semibold">Back to Privluxe</span>
          </Link>
          <div className="flex items-center gap-2">
            <Shield className="h-5 w-5" style={{ color: '#7C3AED' }} />
            <span className="text-sm" style={{ color: 'rgba(229,231,235,0.6)' }}>Legal</span>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        {/* Hero */}
        <div className="text-center mb-12">
          <div className="w-16 h-16 rounded-2xl mx-auto mb-6 flex items-center justify-center" style={{ background: 'linear-gradient(135deg, rgba(239,68,68,0.2) 0%, rgba(245,158,11,0.2) 100%)', border: '1px solid rgba(239,68,68,0.3)' }}>
            <Scale className="h-8 w-8" style={{ color: '#EF4444' }} />
          </div>
          <h1 className="text-4xl font-bold text-white mb-4">Acceptable Use Policy</h1>
          <p style={{ color: 'rgba(229,231,235,0.6)' }}>Last updated: {lastUpdated}</p>
        </div>

        {/* Introduction */}
        <div className="p-6 rounded-2xl mb-12" style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)' }}>
          <p className="text-lg text-center" style={{ color: '#E5E7EB' }}>
            This Acceptable Use Policy outlines the rules and guidelines for using Privluxe.<br />
            <span style={{ color: '#FCA5A5' }}>Violations may result in immediate account termination.</span>
          </p>
        </div>

        {/* Sections */}
        <div className="space-y-8">
          {/* Prohibited Content */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(239,68,68,0.2)' }}>
                <Ban className="h-5 w-5" style={{ color: '#EF4444' }} />
              </div>
              <h2 className="text-2xl font-bold text-white">Prohibited Content</h2>
            </div>
            <div className="space-y-4" style={{ color: 'rgba(229,231,235,0.8)' }}>
              <p>You may NOT use Privluxe to create, share, or distribute:</p>
              <div className="grid gap-3">
                {[
                  "Child sexual abuse material (CSAM) or any content sexualizing minors",
                  "Non-consensual intimate images or revenge pornography",
                  "Content promoting terrorism, violence, or extremism",
                  "Hate speech targeting individuals based on race, religion, gender, sexuality, or disability",
                  "Content that promotes self-harm or suicide",
                  "Malware, viruses, or other harmful code",
                  "Stolen personal data, credentials, or financial information",
                  "Counterfeit goods or fraudulent schemes",
                  "Content infringing on intellectual property rights"
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3 p-3 rounded-lg" style={{ background: 'rgba(239,68,68,0.05)' }}>
                    <XCircle className="h-5 w-5 shrink-0 mt-0.5" style={{ color: '#EF4444' }} />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Prohibited Activities */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(245,158,11,0.2)' }}>
                <UserX className="h-5 w-5" style={{ color: '#F59E0B' }} />
              </div>
              <h2 className="text-2xl font-bold text-white">Prohibited Activities</h2>
            </div>
            <div className="space-y-4" style={{ color: 'rgba(229,231,235,0.8)' }}>
              <p>You may NOT use Privluxe for:</p>
              <div className="grid gap-3">
                {[
                  "Human trafficking, exploitation, or escort services",
                  "Drug trafficking or sale of controlled substances",
                  "Money laundering or financial fraud",
                  "Phishing, scams, or social engineering attacks",
                  "Harassment, stalking, bullying, or threats against others",
                  "Doxxing or sharing others' personal information without consent",
                  "Impersonating other individuals or organizations",
                  "Circumventing security measures or exploiting vulnerabilities",
                  "Spamming or sending unsolicited bulk messages",
                  "Any activity that violates applicable laws"
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3 p-3 rounded-lg" style={{ background: 'rgba(245,158,11,0.05)' }}>
                    <XCircle className="h-5 w-5 shrink-0 mt-0.5" style={{ color: '#F59E0B' }} />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Permitted Uses */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(34,197,94,0.2)' }}>
                <CheckCircle className="h-5 w-5" style={{ color: '#22C55E' }} />
              </div>
              <h2 className="text-2xl font-bold text-white">Permitted Uses</h2>
            </div>
            <div className="space-y-4" style={{ color: 'rgba(229,231,235,0.8)' }}>
              <p>Privluxe is designed for:</p>
              <div className="grid gap-3">
                {[
                  "Private personal communications between consenting adults",
                  "Sharing media with trusted contacts",
                  "Voice and video calls with friends and family",
                  "Professional or business communications",
                  "Any lawful purpose that respects others' rights"
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3 p-3 rounded-lg" style={{ background: 'rgba(34,197,94,0.05)' }}>
                    <CheckCircle className="h-5 w-5 shrink-0 mt-0.5" style={{ color: '#22C55E' }} />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Enforcement */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(124,58,237,0.2)' }}>
                <AlertTriangle className="h-5 w-5" style={{ color: '#A78BFA' }} />
              </div>
              <h2 className="text-2xl font-bold text-white">Enforcement</h2>
            </div>
            <div className="space-y-4" style={{ color: 'rgba(229,231,235,0.8)' }}>
              <p>Violations of this policy may result in:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Warning and content removal</li>
                <li>Temporary suspension of account access</li>
                <li>Permanent account termination</li>
                <li>Reporting to law enforcement authorities</li>
                <li>Legal action where appropriate</li>
              </ul>
              <p>We reserve the right to take action at our sole discretion and without prior notice.</p>
            </div>
          </section>

          {/* Reporting */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(34,211,238,0.2)' }}>
                <FileWarning className="h-5 w-5" style={{ color: '#22D3EE' }} />
              </div>
              <h2 className="text-2xl font-bold text-white">Reporting Violations</h2>
            </div>
            <div className="space-y-4" style={{ color: 'rgba(229,231,235,0.8)' }}>
              <p>If you encounter content or behavior that violates this policy, please report it immediately:</p>
              <div className="p-4 rounded-lg" style={{ background: 'rgba(255,255,255,0.05)' }}>
                <p><strong className="text-white">Report Abuse</strong></p>
                <p>Email: abuse@privluxe.com</p>
                <p>In-app: Use the "Report" feature on any message or user profile</p>
              </div>
              <p>All reports are reviewed by our Trust & Safety team and handled confidentially.</p>
            </div>
          </section>
        </div>

        {/* Related Links */}
        <div className="mt-16 p-6 rounded-2xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
          <h3 className="text-lg font-semibold text-white mb-4">Related Documents</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <Link to="/terms" className="p-4 rounded-xl text-center transition-all hover:scale-105" style={{ background: 'rgba(124,58,237,0.1)', border: '1px solid rgba(124,58,237,0.2)' }}>
              <Shield className="h-6 w-6 mx-auto mb-2" style={{ color: '#A78BFA' }} />
              <span className="text-sm text-white">Terms & Conditions</span>
            </Link>
            <Link to="/privacy" className="p-4 rounded-xl text-center transition-all hover:scale-105" style={{ background: 'rgba(34,211,238,0.1)', border: '1px solid rgba(34,211,238,0.2)' }}>
              <Shield className="h-6 w-6 mx-auto mb-2" style={{ color: '#22D3EE' }} />
              <span className="text-sm text-white">Privacy Policy</span>
            </Link>
            <Link to="/law-enforcement" className="p-4 rounded-xl text-center transition-all hover:scale-105" style={{ background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.2)' }}>
              <Scale className="h-6 w-6 mx-auto mb-2" style={{ color: '#F59E0B' }} />
              <span className="text-sm text-white">Law Enforcement</span>
            </Link>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t mt-16" style={{ borderColor: 'rgba(255,255,255,0.05)' }}>
        <div className="max-w-4xl mx-auto px-4 py-8 text-center">
          <p style={{ color: 'rgba(229,231,235,0.4)' }}>© 2026 Privluxe. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
